import { formatPersonName } from "@/lib/format";
import { sponsorship } from "@/lib/lecturehub/sponsor";
import type { ContentView } from "./events";

/**
 * The `content_view` each detail page reports (lib/analytics/events.ts), built
 * from the record it already loaded. Pure: the server builds it, the client
 * component only sends it.
 */

type Person = { name?: string | null; surname?: string | null };
type Named = { name?: string | null };
type SponsorFields = NonNullable<Parameters<typeof sponsorship>[0]>;

// The section a reader would name — GA's built-in content group.
const GROUPS: Record<string, string> = {
  news: "news",
  announcement: "news",
  article: "articles",
  course: "courses",
  event: "events",
  ott: "videos",
  podcast: "podcasts",
};

function view(
  contentType: string,
  id: string,
  title: string | null | undefined,
  extra: {
    categories?: Named[] | null;
    authors?: Person[] | null;
    publishedAt?: string | null;
    sponsor?: SponsorFields | null;
  },
): ContentView {
  const s = sponsorship(extra.sponsor);
  const author = (extra.authors ?? []).map((a) => formatPersonName(a)).filter(Boolean);
  return {
    content_type: contentType,
    content_id: id,
    content_title: (title ?? "").slice(0, 100),
    content_group: GROUPS[contentType] ?? contentType,
    content_category: (extra.categories ?? []).find((c) => c.name)?.name ?? undefined,
    author: author.length ? author.join(", ").slice(0, 100) : undefined,
    published_date: extra.publishedAt ? extra.publishedAt.slice(0, 10) : undefined,
    sponsored: s?.kind ?? "none",
    sponsor_name: s?.name ?? undefined,
  };
}

/** A news item or announcement (`CommunicationPost`). */
export function newsContentView(
  post: {
    id: string;
    type?: string | null;
    title?: string | null;
    publishedAt?: string | null;
    categories?: Named[] | null;
    instructors?: Person[] | null;
  } & SponsorFields,
): ContentView {
  const type = (post.type ?? "").toLowerCase() === "announcement" ? "announcement" : "news";
  return view(type, post.id, post.title, {
    categories: post.categories,
    authors: post.instructors,
    publishedAt: post.publishedAt,
    sponsor: post,
  });
}

/** An article (a module of type post). */
export function articleContentView(m: {
  id: string;
  name?: string | null;
  categories?: Named[] | null;
  post?:
    | ({
        publishedAt?: string | null;
        instructors?: Person[] | null;
      } & SponsorFields)
    | null;
}): ContentView {
  return view("article", m.id, m.name, {
    categories: m.categories,
    authors: m.post?.instructors,
    publishedAt: m.post?.publishedAt,
    sponsor: m.post,
  });
}

/** A course, event, video or podcast detail page. */
export function moduleContentView(m: {
  id: string;
  name?: string | null;
  refType: string;
  categories?: Named[] | null;
}): ContentView {
  return view(m.refType, m.id, m.name, { categories: m.categories });
}
